import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, EMPTY, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilKeyChanged, map, switchMap } from 'rxjs/operators';
import { StatusCodes } from 'http-status-codes';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DsBannerState, DsButtonVariants, DsInputStates, DsModalService } from '@levelaccess/design-system';

import { $task, TaskPriority, TaskSource, TASK_STATUS_ON_CREATE } from '../../../../../../../../shared/constants/task';
import { FeatureFlagCollection } from '../../../../../../../../shared/interfaces/feature-flag.interface';
import { taskStatusPolicy } from '../../../../../../../../shared/utils/task-status.policy';
import { TranslateService } from '../../../../../../translate/translate.service';
import { CustomValidators } from '../../../../../../services/helpers/form-custom-validators';
import { IProject } from '../../../../../../../../shared/interfaces/project.interface';
import { ProjectService } from '../../../../../../services/project.service';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';
import {
  IIssueIntegrationConnection,
  IIssueTrackingIssueType,
} from '../../../../../../../../shared/interfaces/issue-integration-connection.interface';
import { IssueTrackingService } from '../../../../../../services/issue-tracking.service';
import { TaskPriorityOptions } from '../../../../projects/tasks/upsert-task-modal/upsert-task-modal.component';
import { ErrorMessageService } from '../../../../../../services/error-message.service';
import { ITask } from '../../../../../../../../shared/interfaces/task.interface';
import { ManualAuditIssueService } from '../../../../../../services/manual-audit-issue.service';
import { $auditFinding } from '../../../../../../../../shared/constants/audit-finding';
import { Api } from '../../../../../../../../shared/constants/api';
import { NotificationService } from '../../../../../../services/notification.service';
import { NotificationPosition } from '../../../../../../models/notification.model';
import { IPopulatedManualFinding } from '../../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { IUploadClient } from '../../../../../../../../shared/interfaces/uploads.interface';
import { $uploads } from '../../../../../../../../shared/constants/uploads';
import {
  ITaskCreateFromSuccessCriteriaRequest,
  ITaskCreateRequest,
} from '../../../../../../../../shared/interfaces/task.requests.interface';
import { $project } from '../../../../../../../../shared/constants/project';
import { IWorkspace } from '../../../../../../../../shared/interfaces/workspace.interface';
import { WorkspaceService } from '../../../../../../services/workspace.service';
import { ShortLinkService } from '../../../../../../services/short-link.service';
import { $workspace } from '../../../../../../../../shared/constants/workspace';
import { $issueIntegrationType } from '../../../../../../../../shared/constants/issue-tracking';
import { $issueIntegrationConnection } from '../../../../../../../../shared/constants/issue-tracking-connection';

import { UpsertTaskHelper } from '../../../../projects/tasks/upsert-task-slideout/upsert-task-helper';
import { TenantService } from '../../../../../../services/tenant.service';
import { ProjectUtility } from '../../../../../../utility/project.utility';

@Component({
  selector: 'app-bulk-task-creation-modal',
  templateUrl: './bulk-task-creation-slideout.component.html',
  styleUrls: ['./bulk-task-creation-slideout.component.scss'],
  providers: [UpsertTaskHelper],
  standalone: false,
})
export class BulkTaskCreationSlideoutComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  private workspace: IWorkspace;
  private selectedProject: IProject;

  @Input() public findings: IPopulatedManualFinding[] = [];
  @Input() public successCriteriaIds: string[] = [];
  @Input() public workspaceId: string = '';
  @Input() public manualEvaluationId: string;
  @Input() public activatedRoute: ActivatedRoute;
  @Output() public created: EventEmitter<ITask[] | null> = new EventEmitter();

  public form: UntypedFormGroup;
  public $task: typeof $task = $task;
  public $project: typeof $project = $project;
  public taskPriorityOptions: TaskPriorityOptions[];

  public availableProjects$: BehaviorSubject<IProject[]>;
  public selectedProject$: BehaviorSubject<IProject>;
  public issueIntegrationProjectLabel$: Observable<string>;
  public selectIssueTypeLabel$: Observable<string>;
  public issueTrackingIssueTypes$: Observable<IIssueTrackingIssueType[]>;
  public loadingError$: Subject<boolean> = new Subject();
  public pushToIssueTracking: boolean = false;
  public formFieldErrors: Record<string, string>;
  public readonly DsInputStates: typeof DsInputStates;
  public readonly DsButtonVariants: typeof DsButtonVariants;

  protected readonly projectCreatedSuccessMessage$: BehaviorSubject<string | null>;
  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private projectService: ProjectService,
    private issueTrackingService: IssueTrackingService,
    private errorMessageService: ErrorMessageService,
    private element: ElementRef<Element>,
    private manualAuditIssueService: ManualAuditIssueService,
    private router: Router,
    private notificationService: NotificationService,
    private workspaceService: WorkspaceService,
    private shortLinkService: ShortLinkService,
    private changeDetector: ChangeDetectorRef,

    public modal: NgbActiveOffcanvas,
    public modalService: DsModalService,
    private upsertTaskHelper: UpsertTaskHelper,
    private tenantService: TenantService,
  ) {
    this.form = this.createForm();
    this.selectedProject$ = new BehaviorSubject<IProject>(null);
    this.taskPriorityOptions = Object.keys(TaskPriority).map((priority: string) => {
      return { priorityValue: priority, name: `label_task_priority_${priority}` };
    });
    this.formFieldErrors = {};
    this.DsInputStates = DsInputStates;
    this.DsButtonVariants = DsButtonVariants;
    this.availableProjects$ = new BehaviorSubject<IProject[]>([]);
    this.projectCreatedSuccessMessage$ = new BehaviorSubject<string | null>(null);
  }

  private createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      [$task.projectId]: this.formBuilder.control(null, [CustomValidators.required]),
      [$task.status]: this.formBuilder.control(
        this.translateService.instant(taskStatusPolicy(false).labelKey(TASK_STATUS_ON_CREATE)),
      ),
      [$task.priority]: this.formBuilder.control(TaskPriority.medium),
    });
  }

  public get canPushToIssueTracking$(): Observable<boolean> {
    return this.upsertTaskHelper.canPushToIssueTracking$(this.form);
  }

  public get selectedProjectIsGithub(): boolean {
    const project: IProject = this.form.get($task.projectId)?.value;
    return (
      project?.[$project.issueIntegrationConnection]?.[$issueIntegrationConnection.integrationType] ===
      $issueIntegrationType.github
    );
  }

  public isIssueTrackingTypesSelectorVisible(): boolean {
    const project: IProject = this.form.get($task.projectId)?.value;
    return (
      this.pushToIssueTracking &&
      project?.[$project.issueIntegrationConnection]?.[$issueIntegrationConnection.integrationType] !==
        $issueIntegrationType.asana
    );
  }

  private newProjectCreated(project: IProject): void {
    this.availableProjects$.next([project, ...this.availableProjects$.value]);
    this.form.get($task.projectId).setValue(project);

    this.projectCreatedSuccessMessage$.next(this.translateService.instant('project_created'));
    this.changeDetector.detectChanges();
  }

  public onCreateProjectSuccessBannerClosed(): void {
    this.projectCreatedSuccessMessage$.next(null);
  }

  public createNewProject(): void {
    this.subscription.add(this.upsertTaskHelper.createNewProject(this.workspaceId).subscribe(this.newProjectCreated.bind(this)));
  }

  public pushToIssueTrackingChange(value: boolean): void {
    this.pushToIssueTracking = value;
    const project: IProject = this.form.get($task.projectId)?.value;

    this.issueTrackingIssueTypes$ = this.isIssueTrackingTypesSelectorVisible()
      ? this.issueTrackingService
          .getIssueTrackingIssueTypes(
            project[$project.issueIntegrationConnectionId],
            project[$project.issueIntegrationProject][$project.issueIntegrationProjectId],
          )
          .pipe(
            catchError((err: any) => {
              if (err.status === StatusCodes.TOO_MANY_REQUESTS) {
                this.notificationService.error(
                  this.translateService.instant('too_many_requests'),
                  NotificationPosition.Toast,
                  true,
                );
              }
              this.loadingError$.next();
              return EMPTY;
            }),
          )
      : null;

    if (this.isIssueTrackingTypesSelectorVisible()) {
      this.form.addControl($task.issueIntegrationType, this.formBuilder.control(null, [CustomValidators.required]));
    } else {
      this.form.removeControl($task.issueIntegrationType);
    }
  }

  public submit(): void {
    this.form.markAllAsTouched();
    this.updateErrorMessages();
    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    if (SharedCommonUtility.notNullishOrEmpty(this.findings)) {
      this.submitFindings();
    } else {
      this.submitSuccessCriterias();
    }
  }

  private submitSuccessCriterias(): void {
    const projectId: string = this.form.get($task.projectId).value._id;
    const taskRequest: ITaskCreateFromSuccessCriteriaRequest = {
      successCriteriaIds: this.successCriteriaIds,
      [$task.pushTaskToIssueTracking]: String(this.pushToIssueTracking),
      [$task.priority]: this.form.get($task.priority).value,
      manualEvaluationId: this.manualEvaluationId,
      eventPagePath: `/${this.activatedRoute.snapshot.routeConfig.path}`,
    };

    if (this.isIssueTrackingTypesSelectorVisible()) {
      taskRequest[$task.issueIntegrationType] = this.form.get($task.issueIntegrationType).value;
    }

    this.subscription.add(
      this.projectService.createTasksForSuccessCriterias(this.workspaceId, projectId, taskRequest).subscribe({
        next: (tasks: ITask[]) => {
          this.created.emit(tasks);
          this.modalService.closeCurrentModal();
        },
        error: () => {
          this.created.emit(null);
          this.modalService.closeCurrentModal();
        },
      }),
    );
  }

  private submitFindings(): void {
    const projectId: string = this.form.get($task.projectId).value._id;
    const baseUrl: string = this.shortLinkService.getBaseUrl(this.workspace[$workspace.tenant]);
    const taskRequests: ITaskCreateRequest[] = [];

    for (const finding of this.findings) {
      const linkToFinding: string = this.router
        .createUrlTree([finding[$auditFinding.issueId], Api.view], {
          relativeTo: this.activatedRoute,
          queryParams: this.activatedRoute.snapshot.queryParams,
        })
        .toString();
      let attachmentIds: string[] = [];
      if (SharedCommonUtility.notNullish(finding[$auditFinding.attachment])) {
        attachmentIds = finding[$auditFinding.attachment].map(
          (attachment: IUploadClient): string => attachment[$uploads._id] as string,
        );
      }

      const taskRequest: ITaskCreateRequest = {
        source: TaskSource.manual,
        [$task.name]: finding[$auditFinding.summary],
        [$task.description]: this.manualAuditIssueService.generateTaskDescription(baseUrl, finding, linkToFinding),
        [$task.priority]: this.form.get($task.priority).value,
        [$task.pushTaskToIssueTracking]: String(this.pushToIssueTracking),
        [$task.auditFindingIds]: [finding[$auditFinding._id]],
        [$task.attachments]: attachmentIds,
      };

      if (this.isIssueTrackingTypesSelectorVisible()) {
        taskRequest[$task.issueIntegrationType] = this.form.get($task.issueIntegrationType).value;
      }

      taskRequests.push(taskRequest);
    }

    this.subscription.add(
      this.projectService.createTasks(this.workspaceId, projectId, taskRequests).subscribe({
        next: (resTasks: ITask[]) => {
          const tasks: ITask[] = resTasks ?? [];

          const tasksNamesToShow: number = 10;
          const mapProjectId = (task: ITask): string => {
            return task[$task.taskProjectId];
          };
          const commaSeparated: string = tasks.slice(0, tasksNamesToShow).map(mapProjectId).join(', ');

          if (tasks.length > tasksNamesToShow) {
            const moreToShow: number = tasks.length - tasksNamesToShow;
            this.notificationService.success(
              this.translateService.instant('tasks_created_successfully_and_n_more', [commaSeparated, String(moreToShow)]),
              NotificationPosition.Toast,
              true,
            );
          } else {
            this.notificationService.success(
              this.translateService.instantHtml('tasks_created_successfully_view_link', [
                tasks.length,
                `/${Api.projects}/${this.form.get($task.projectId).value._id}/${Api.tasks}`,
                this.selectedProject?.name || this.form.get($task.projectId).value._id,
              ]),
              NotificationPosition.Toast,
              true,
              undefined,
              false,
              true,
            );
          }

          this.created.emit(tasks);
          this.modalService.closeCurrentModal();
        },
        error: () => {
          this.created.emit(null);
          this.notificationService.error(this.translateService.instant('tasks_creation_error'), NotificationPosition.Toast, true);
        },
      }),
    );
  }

  public onProjectChange(project: IProject): void {
    this.selectedProject$.next(project);
    this.selectedProject = project;
  }

  public updateErrorMessages(): void {
    this.formFieldErrors = this.upsertTaskHelper.updateErrorMessages(this.form);
  }

  protected projectPlaceholder(availableProjects: IProject[]): string {
    const key = availableProjects.length > 0 ? 'select_a_project' : 'no_projects_created';
    return this.translateService.instant(key);
  }

  protected projectInputError(availableProjects: IProject[]): string {
    if (availableProjects.length > 0) {
      return this.formFieldErrors[$task.projectId];
    }
    return this.translateService.instant('create_project_to_continue');
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.tenantService
        .getTenantFeatureFlag(FeatureFlagCollection.separatedBacklogReopenedStatus)
        .pipe(catchError(() => of(false)))
        .subscribe((separated: boolean) => {
          this.form
            .get($task.status)
            ?.setValue(this.translateService.instant(taskStatusPolicy(separated).labelKey(TASK_STATUS_ON_CREATE)));
        }),
    );
    this.subscription.add(
      this.projectService.getProjects(this.workspaceId).subscribe((projects: IProject[]) => {
        const activeProjects: IProject[] = ProjectUtility.getActiveProjects(projects);
        this.availableProjects$.next(activeProjects);
      }),
    );

    this.subscription.add(
      this.form.get($task.projectId).valueChanges.subscribe(() => {
        this.pushToIssueTracking = false; // NOTE: uncheck checkbox when switching projects
      }),
    );

    this.subscription.add(
      this.workspaceService.getWorkspace(this.workspaceId).subscribe((workspace: IWorkspace) => {
        this.workspace = workspace;
      }),
    );

    const issueIntegrationConnection$: Observable<IIssueIntegrationConnection> = this.selectedProject$.pipe(
      distinctUntilKeyChanged($project._id),
      switchMap((project: IProject): Observable<IIssueIntegrationConnection> => {
        if (SharedCommonUtility.notNullish(project[$project.issueIntegrationConnectionId])) {
          return this.issueTrackingService.getIssueTrackingConnectionInfo(project[$project.issueIntegrationConnectionId]);
        }
        return of(null);
      }),
    );

    this.issueIntegrationProjectLabel$ = issueIntegrationConnection$.pipe(
      map((connection: IIssueIntegrationConnection): string => {
        const integrationType: $issueIntegrationType = connection
          ? connection[$issueIntegrationConnection.integrationType]
          : null;
        return integrationType ? this.translateService.instant(`push_to_${integrationType}`) : this.translateService.instant('');
      }),
    );

    this.selectIssueTypeLabel$ = issueIntegrationConnection$.pipe(
      map((connection: IIssueIntegrationConnection): string => {
        const integrationType: $issueIntegrationType = connection
          ? connection[$issueIntegrationConnection.integrationType]
          : null;
        return integrationType
          ? this.translateService.instant(
              'select_an_issue_integration_issue_type',
              this.translateService.instant(integrationType),
            )
          : this.translateService.instant('');
      }),
    );

    this.subscription.add(this.form.valueChanges.subscribe(this.updateErrorMessages.bind(this)));
    this.subscription.add(this.form.get($task.projectId).valueChanges.subscribe(this.onProjectChange.bind(this)));
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
